import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../services/axiosInstance";
import { trialNotice } from "../../utils/trialNotice";

/**
 * Banner persistente de estado de suscripción.
 * - Pago pendiente (spec 007, US1 / FR-003, D4): gracia → regularizar.
 * - Prueba de 14 días (spec 031, US4 / FR-008): countdown permanente con CTA
 *   a elegir plan, con urgencia progresiva según la cadencia del manual §3.
 * El estado sale de GET /account/subscription (status + isTrial/trialDaysLeft).
 */
const CACHE_TTL_MS = 10 * 60_000;
let cachedStatus: string | null = null;
let cachedTrial: { isTrial: boolean; daysLeft: number | null; endsAt?: string | null } = {
  isTrial: false,
  daysLeft: null,
  endsAt: null,
};
let cacheExpiresAt = 0;

const GraceBanner = () => {
  const [inGrace, setInGrace] = useState(cachedStatus === "gracia");
  const [trial, setTrial] = useState(cachedTrial);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    const apply = () => {
      if (!active) return;
      setInGrace(cachedStatus === "gracia");
      setTrial(cachedTrial);
    };
    const load = async () => {
      if (cacheExpiresAt > Date.now()) {
        apply();
        return;
      }
      try {
        const res = await axiosInstance.get("/api/v2/account/subscription");
        const data = res.data?.data;
        cachedStatus = data?.status ?? null;
        cachedTrial = {
          isTrial: Boolean(data?.isTrial),
          daysLeft: data?.trialDaysLeft ?? null,
          endsAt: data?.trialEndsAt ?? null,
        };
      } catch {
        cachedStatus = null;
        cachedTrial = { isTrial: false, daysLeft: null };
      }
      cacheExpiresAt = Date.now() + CACHE_TTL_MS;
      apply();
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  if (dismissed) return null;

  const baseStyle: React.CSSProperties = {
    position: "sticky",
    top: 0,
    zIndex: 1200,
    borderBottom: "1px solid",
    padding: "0.5rem 1rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.75rem",
    fontSize: "0.9rem",
  };
  const btnStyle: React.CSSProperties = {
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "0.3rem 0.9rem",
    fontWeight: 600,
    cursor: "pointer",
    fontSize: "0.85rem",
  };
  const closeStyle: React.CSSProperties = {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: "1rem",
  };

  // Prueba de 14 días (prioridad sobre gracia: en prueba no hay cobros)
  if (trial.isTrial) {
    // F-R1-4: 0 días = la prueba YA terminó (no "termina hoy").
    const notice = trialNotice(trial.daysLeft, trial.endsAt ?? null);
    const urgent = notice.urgent;
    const message = notice.text;
    return (
      <div
        role="status"
        style={{
          ...baseStyle,
          background: urgent ? "#fdf1e7" : "#f0edfe",
          borderBottomColor: urgent ? "#c2410c" : "#937cf4",
          color: urgent ? "#9a3412" : "#45348e",
        }}
      >
        <span>{message}</span>
        <button
          onClick={() => navigate("/mi-cuenta/planes")}
          style={{ ...btnStyle, background: urgent ? "#c2410c" : "#45348e" }}
        >
          Elegir plan
        </button>
        <button
          aria-label="Cerrar aviso"
          onClick={() => setDismissed(true)}
          style={{ ...closeStyle, color: urgent ? "#9a3412" : "#45348e" }}
        >
          ✕
        </button>
      </div>
    );
  }

  if (!inGrace) return null;

  return (
    <div
      role="status"
      style={{
        ...baseStyle,
        background: "#fef3c7",
        borderBottomColor: "#f59e0b",
        color: "#92400e",
      }}
    >
      <span>
        Tu pago está pendiente. Regularízalo para no perder acceso a Prikly.
      </span>
      <button
        onClick={() => navigate("/mi-cuenta/gestionar-suscripcion")}
        style={{ ...btnStyle, background: "#b45309" }}
      >
        Pagar ahora
      </button>
      <button
        aria-label="Cerrar aviso"
        onClick={() => setDismissed(true)}
        style={{ ...closeStyle, color: "#92400e" }}
      >
        ✕
      </button>
    </div>
  );
};

export default GraceBanner;
